import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, Phone, Mail, User, Truck as TruckIcon, Weight, Container } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { supabase } from '@/integrations/supabase/client';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [userType, setUserType] = useState<'customer' | 'carrier' | null>(null);
  const [carrierType, setCarrierType] = useState<'carrier_company' | 'broker' | 'driver'>('carrier_company');
  const [phone, setPhone] = useState('');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [mainBranches, setMainBranches] = useState<string[]>([]);
  const [shippedItemsDesc, setShippedItemsDesc] = useState('');
  const [preferredVehicles, setPreferredVehicles] = useState<string[]>([]);
  const [averageWeightTons, setAverageWeightTons] = useState<string>('');
  const [vehiclesCount, setVehiclesCount] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { signIn, signUp, user, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const saudiCities = [
    "Riyadh", "Jeddah", "Dammam", "Mecca", "Medina", "Khobar", "Dhahran",
    "Taif", "Buraidah", "Tabuk", "Hail", "Khamis Mushait", "Hofuf", "Mubarraz",
    "Najran", "Yanbu", "Jubail", "Abha", "Arar", "Sakaka"
  ];

  const vehicleTypes = [
    'truck',
    'dyna'
  ];

  const handleCityToggle = (city: string) => {
    const updatedBranches = mainBranches.includes(city)
      ? mainBranches.filter(c => c !== city)
      : [...mainBranches, city];
    setMainBranches(updatedBranches);
  };

  const handleVehicleToggle = (vehicle: string) => {
    const updatedVehicles = preferredVehicles.includes(vehicle)
      ? preferredVehicles.filter(v => v !== vehicle)
      : [...preferredVehicles, vehicle];
    setPreferredVehicles(updatedVehicles);
  };

  const getVehicleIcon = (vehicle: string) => {
    switch (vehicle) {
      case 'truck':
        return <img src="/lovable-uploads/4d93d72f-1e51-4a67-9f87-c64943869636.png" alt="Truck" className="h-6 w-6" />;
      case 'dyna':
        return <TruckIcon className="h-5 w-5" />; // Smaller vehicle  
      default:
        return <TruckIcon className="h-5 w-5" />;
    }
  };

  const formatSaudiPhone = (input: string) => {
    // Remove all non-digit characters
    let cleaned = input.replace(/\D/g, '');
    
    // If already starts with 0966, keep as is
    if (cleaned.startsWith('0966')) {
      return cleaned;
    }
    // If starts with 966, add 0 prefix
    else if (cleaned.startsWith('966')) {
      return '0' + cleaned;
    }
    // If starts with 0, replace with 0966
    else if (cleaned.startsWith('0')) {
      return '0966' + cleaned.substring(1);
    }
    // If starts with 5 (common Saudi mobile prefix), add 0966
    else if (cleaned.startsWith('5') && cleaned.length === 9) {
      return '0966' + cleaned;
    }
    
    return cleaned;
  };

  const formatPhoneDisplay = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.startsWith('0966')) {
      // Format as +966 XX XXX XXXX (remove leading 0 for display)
      const countryCode = cleaned.substring(1, 4); // "966"
      const firstPart = cleaned.substring(4, 6);
      const secondPart = cleaned.substring(6, 9);
      const thirdPart = cleaned.substring(9, 13);
      
      let formatted = `+${countryCode}`;
      if (firstPart) formatted += ` ${firstPart}`;
      if (secondPart) formatted += ` ${secondPart}`;
      if (thirdPart) formatted += ` ${thirdPart}`;
      
      return formatted;
    }
    
    return phone;
  };

  useEffect(() => {
    const checkUserSetupAndRedirect = async () => {
      if (user && !loading) {
        const returnUrl = searchParams.get('returnUrl');
        
        // Check if user has completed setup (has organization with username)
        const { data: orgIds } = await supabase
          .rpc('get_user_org_ids_bypass_rls', { user_uuid: user.id });

        if (orgIds && orgIds.length > 0) {
          // Get organization details to check if username is set
          const { data: org } = await supabase
            .from('organizations')
            .select('username')
            .eq('id', orgIds[0])
            .single();

          if (org && org.username) {
            // User is fully set up, safe to redirect to returnUrl
            if (returnUrl) {
              const decodedReturnUrl = decodeURIComponent(returnUrl);
              navigate(decodedReturnUrl);
              return;
            }
          }
        }
        
        // If no returnUrl or user needs setup, go to home page
        // The Index page will handle username setup and returnUrl preservation  
        if (returnUrl) {
          navigate(`/?returnUrl=${encodeURIComponent(returnUrl)}`);
        } else {
          navigate('/');
        }
      }
    };

    checkUserSetupAndRedirect();
  }, [user, loading, navigate, searchParams]);

  // Auto-select carrier type when coming from carrier invitation
  useEffect(() => {
    const userTypeParam = searchParams.get('userType');
    if (userTypeParam === 'carrier_company' || userTypeParam === 'broker' || userTypeParam === 'driver') {
      setUserType('carrier');
      setCarrierType(userTypeParam as 'carrier_company' | 'broker' | 'driver');
    }
  }, [searchParams]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await signIn(email, password);
    // Keep loading state true until navigation occurs (useEffect will handle it)
    // Only set loading to false if there was an error
    if (result.error) {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Ensure user has selected a type
    if (!userType) {
      return; // Form validation will prevent submission
    }
    
    // Ensure customer has filled shipped items description
    if (userType === 'customer' && !shippedItemsDesc.trim()) {
      return; // Form validation will prevent submission
    }
    
    setIsLoading(true);
    
    // Map UI user types to database enum values
    const dbUserType = userType === 'customer' ? 'business_customer' : carrierType;
    
    // Convert tons to kg for storage
    const averageWeightKg = averageWeightTons ? Math.round(parseFloat(averageWeightTons) * 1000) : null;
    
    const userData = {
      full_name: fullName,
      user_type: dbUserType,
      phone,
      company_name: companyName,
      main_branches: mainBranches.join(','), // Convert array to comma-separated string
      shipped_items_desc: shippedItemsDesc,
      preferred_vehicles: preferredVehicles.join(','), // Convert array to comma-separated string
      average_weight_kg: averageWeightKg?.toString(),
      vehicles_count: vehiclesCount || null
    };
    
    const result = await signUp(email, password, userData);
    
    // Keep loading state true until navigation occurs (useEffect will handle it)
    // Only set loading to false if there was an error
    if (result.error) {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md shadow-[var(--shadow-card)]">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-2">
            <div className="bg-primary p-3 rounded-lg">
              <img 
                src="/lovable-uploads/94433138-910b-4d0f-a142-73e70d167ac6.png" 
                alt="Shohnat" 
                className="h-12 w-auto" 
              />
            </div>
          </div>
          <div className="space-y-1">
            <CardTitle className="text-2xl font-bold">{t('auth.welcomeToShohnat')}</CardTitle>
            <CardDescription>
              {t('auth.signInDescription')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">{t('auth.signIn')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth.signUp')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                 <div className="space-y-2">
                   <Label htmlFor="email" className="ltr:text-left rtl:text-right">{t('auth.email')}</Label>
                   <Input
                     id="email"
                     type="email"
                     value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     required
                   />
                 </div>
                 <div className="space-y-2">
                   <Label htmlFor="password" className="ltr:text-left rtl:text-right">{t('auth.password')}</Label>
                   <Input
                     id="password"
                     type="password"
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     required
                   />
                 </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t('auth.signingIn') : t('auth.signIn')}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full-name" className="ltr:text-left rtl:text-right">{t('auth.yourName')}</Label>
                  <div className="relative">
                    <User className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="full-name"
                      placeholder={t('auth.enterFullName')}
                      className="ltr:pl-10 rtl:pr-10"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="ltr:text-left rtl:text-right">{t('auth.phoneNumber')}</Label>
                  <div className="relative">
                    <Phone className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+966 50 123 4567"
                      className="ltr:pl-10 rtl:pr-10"
                      value={phoneDisplay}
                      onChange={(e) => {
                        const formatted = formatSaudiPhone(e.target.value);
                        const display = formatPhoneDisplay(formatted);
                        setPhone(formatted);
                        setPhoneDisplay(display);
                      }}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="ltr:text-left rtl:text-right">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      className="ltr:pl-10 rtl:pr-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="signup-password" className="ltr:text-left rtl:text-right">{t('auth.password')}</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="ltr:text-left rtl:text-right">{t('auth.iAmA')}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={userType === 'customer' ? 'default' : 'outline'}
                      onClick={() => setUserType('customer')}
                      className="w-full"
                    >
                      {t('auth.customer')}
                    </Button>
                    <Button
                      type="button"
                      variant={userType === 'carrier' ? 'default' : 'outline'}
                      onClick={() => setUserType('carrier')}
                      className="w-full flex items-center gap-2"
                    >
                      <TruckIcon className="h-4 w-4" />
                      {t('auth.carrier')}
                    </Button>
                  </div>
                 </div>

                 {userType && (
                   <>
                     {userType === 'carrier' && (
                       <div className="space-y-2">
                          <Label htmlFor="carrier-type" className="ltr:text-left rtl:text-right">{t('auth.carrierType')}</Label>
                          <Select value={carrierType} onValueChange={(value: 'carrier_company' | 'broker' | 'driver') => setCarrierType(value)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="carrier_company">{t('auth.carrierCompany')}</SelectItem>
                              <SelectItem value="broker">{t('auth.broker')}</SelectItem>
                              <SelectItem value="driver">{t('auth.driver')}</SelectItem>
                            </SelectContent>
                          </Select>
                       </div>
                     )}

                     {(userType === 'customer' || (userType === 'carrier' && carrierType !== 'driver')) && (
                       <div className="space-y-2">
                          <Label htmlFor="company-name" className="ltr:text-left rtl:text-right">{t('auth.companyName')}</Label>
                           <div className="relative">
                             <Building2 className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                             <Input
                               id="company-name"
                               placeholder={t('auth.enterCompanyName')}
                              className="ltr:pl-10 rtl:pr-10"
                              value={companyName}
                              onChange={(e) => setCompanyName(e.target.value)}
                              required
                            />
                          </div>
                       </div>
                     )}

                     {userType === 'customer' && (
                       <>
                         <div className="space-y-2">
                            <Label className="ltr:text-left rtl:text-right">{t('auth.pickupLocations')}</Label>
                            <p className="text-sm text-muted-foreground">{t('auth.selectBranches')}</p>
                           <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-md p-3">
                             {saudiCities.map((city) => (
                               <div key={city} className="flex items-center space-x-2">
                                 <Checkbox
                                   id={`city-${city}`}
                                   checked={mainBranches.includes(city)}
                                   onCheckedChange={() => handleCityToggle(city)}
                                 />
                                 <Label htmlFor={`city-${city}`} className="text-sm font-normal">
                                   {city}
                                 </Label>
                               </div>
                             ))}
                           </div>
                         </div>
                         <div className="space-y-2">
                            <Label htmlFor="shipped-items" className="ltr:text-left rtl:text-right">{t('auth.typesOfItems')}</Label>
                             <Textarea
                               id="shipped-items"
                               placeholder={t('auth.itemsPlaceholder')}
                              value={shippedItemsDesc}
                              onChange={(e) => setShippedItemsDesc(e.target.value)}
                              rows={3}
                              required
                            />
                         </div>

                         <div className="space-y-2">
                            <Label className="ltr:text-left rtl:text-right">
                              <TruckIcon className="inline h-4 w-4 ltr:mr-2 rtl:ml-2" />
                               {t('auth.typicallyOrderedVehicles')}
                             </Label>
                            <p className="text-sm text-muted-foreground">{t('auth.selectVehicleTypes')}</p>
                           <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                             {vehicleTypes.map((vehicle) => (
                               <div key={vehicle} className="flex items-center space-x-2">
                                 <Checkbox
                                   id={`vehicle-${vehicle}`}
                                   checked={preferredVehicles.includes(vehicle)}
                                   onCheckedChange={() => handleVehicleToggle(vehicle)}
                                 />
                                 <Label htmlFor={`vehicle-${vehicle}`} className="text-sm font-normal flex items-center gap-2">
                                   {getVehicleIcon(vehicle)}
                                   {t(`vehicleTypes.${vehicle}`)}
                                 </Label>
                               </div>
                             ))}
                           </div>
                         </div>

                         <div className="space-y-2">
                            <Label htmlFor="average-weight" className="ltr:text-left rtl:text-right">{t('auth.averageShipmentWeight')}</Label>
                             <div className="relative">
                               <Weight className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                 <Input
                                   id="average-weight"
                                   type="number"
                                   step="1"
                                   placeholder="5"
                                   className="ltr:pl-10 rtl:pr-10"
                                   value={averageWeightTons}
                                   onChange={(e) => {
                                     const value = e.target.value;
                                     // Only allow integers
                                     if (value === '' || /^\d+$/.test(value)) {
                                       setAverageWeightTons(value);
                                     }
                                   }}
                                   min="0"
                               />
                             </div>
                            <p className="text-xs text-muted-foreground">{t('auth.enterAverageWeight')}</p>
                         </div>
                       </>
                     )}

                     {userType === 'carrier' && (
                       <>
                         <div className="space-y-2">
                            <Label className="ltr:text-left rtl:text-right">
                              <TruckIcon className="inline h-4 w-4 ltr:mr-2 rtl:ml-2" />
                               {t('auth.typeOfVehicles')}
                             </Label>
                            <p className="text-sm text-muted-foreground">{t('auth.selectVehicleTypesOperate')}</p>
                           <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                             {vehicleTypes.map((vehicle) => (
                               <div key={vehicle} className="flex items-center space-x-2">
                                 <Checkbox
                                   id={`carrier-vehicle-${vehicle}`}
                                   checked={preferredVehicles.includes(vehicle)}
                                   onCheckedChange={() => handleVehicleToggle(vehicle)}
                                 />
                                 <Label htmlFor={`carrier-vehicle-${vehicle}`} className="text-sm font-normal flex items-center gap-2">
                                   {getVehicleIcon(vehicle)}
                                   {t(`vehicleTypes.${vehicle}`)}
                                 </Label>
                               </div>
                             ))}
                           </div>
                         </div>

                         <div className="space-y-2">
                            <Label htmlFor="vehicles-count" className="ltr:text-left rtl:text-right">{t('auth.numberOfVehicles')}</Label>
                             <div className="relative">
                               <TruckIcon className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                               <Input
                                 id="vehicles-count"
                                 type="number"
                                 placeholder="10"
                                 className="ltr:pl-10 rtl:pr-10"
                                 value={vehiclesCount}
                                 onChange={(e) => setVehiclesCount(e.target.value)}
                                 min="0"
                               />
                             </div>
                            <p className="text-xs text-muted-foreground">{t('auth.enterTotalVehicles')}</p>
                         </div>
                       </>
                     )}
                   </>
                 )}

                 {userType && (
                   <Button type="submit" className="w-full" disabled={isLoading}>
                     {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
                   </Button>
                 )}
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;